import React, { useState, useEffect } from 'react';
import { Calendar, X, Clock, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { parseInputDate, toInputDate, toRangeEnd, toRangeStart } from '@/lib/dateRange';

interface DateFiltersProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  onRangeChange?: (start: Date | null, end: Date | null) => void;
  onReset: () => void;
  className?: string;
  compact?: boolean;
}

interface QuickPeriod {
  label: string;
  value: string;
  action: () => void;
}

export const DateFilters: React.FC<DateFiltersProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRangeChange,
  onReset,
  className = "",
  compact = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localStartDate, setLocalStartDate] = useState(toInputDate(startDate));
  const [localEndDate, setLocalEndDate] = useState(toInputDate(endDate));

  const hasFilters = startDate || endDate;

  // Synchroniser l'état local quand les props changent
  useEffect(() => {
    setLocalStartDate(toInputDate(startDate));
    setLocalEndDate(toInputDate(endDate));
  }, [startDate, endDate]);


  const formatDateShort = (date: Date) => {
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short'
    });
  };

  const getPeriodText = () => {
    if (startDate && endDate) {
      const today = new Date();
      const isToday = (
        startDate.getDate() === today.getDate() &&
        startDate.getMonth() === today.getMonth() &&
        startDate.getFullYear() === today.getFullYear() &&
        endDate.getDate() === today.getDate() &&
        endDate.getMonth() === today.getMonth() &&
        endDate.getFullYear() === today.getFullYear()
      );

      if (isToday) {
        return "Aujourd'hui";
      }

      const isSameMonth = startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear();
      if (isSameMonth) {
        return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
      }
      return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
    } else if (startDate) {
      return `Depuis ${formatDateShort(startDate)}`;
    } else if (endDate) {
      return `Jusqu'au ${formatDateShort(endDate)}`;
    }
    return "Toutes les périodes";
  };

  /**
   * Émet une plage en bornes de journées pleines (#47) : la date de début est
   * ramenée à 00:00 et la date de fin poussée à 23:59:59.999, sinon le dernier
   * jour sélectionné est exclu par les filtres SQL (`date <= p_end_date`).
   */
  const emitRange = (start: Date | null, end: Date | null) => {
    const normalizedStart = toRangeStart(start) ?? null;
    const normalizedEnd = toRangeEnd(end) ?? null;

    if (onRangeChange) {
      onRangeChange(normalizedStart, normalizedEnd);
    } else {
      onStartDateChange(normalizedStart);
      onEndDateChange(normalizedEnd);
    }
    setIsOpen(false);
  };

  const setQuickPeriod = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);

    emitRange(start, end);
  };

  const setToday = () => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    emitRange(startOfDay, endOfDay);
  };

  const setCurrentMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    emitRange(start, end);
  };

  const setLastMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);

    emitRange(start, end);
  };

  const setCurrentYear = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear(), 11, 31);

    emitRange(start, end);
  };

  const quickPeriods: QuickPeriod[] = [
    { label: "Aujourd'hui", value: "today", action: setToday },
    { label: "7 derniers jours", value: "7d", action: () => setQuickPeriod(7) },
    { label: "30 derniers jours", value: "30d", action: () => setQuickPeriod(30) },
    { label: "90 derniers jours", value: "90d", action: () => setQuickPeriod(90) },
    { label: "Ce mois-ci", value: "current-month", action: setCurrentMonth },
    { label: "Mois dernier", value: "last-month", action: setLastMonth },
    { label: "Cette année", value: "current-year", action: setCurrentYear },
  ];

  const handleReset = () => {
    onReset();
    setIsOpen(false);
  };

  const handleApplyCustom = () => {
    const start = parseInputDate(localStartDate);
    const end = parseInputDate(localEndDate);

    emitRange(start, end);
  };

  // Version compacte avec popover
  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-9 px-3 text-sm font-normal justify-between min-w-[200px] bg-background/50 border-border/50 hover:bg-background/80 transition-all duration-200"
            >
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className={cn("truncate", !hasFilters ? "text-muted-foreground" : "text-foreground")}>{getPeriodText()}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <Tabs defaultValue="quick" className="w-full">
              <TabsList className="grid w-full grid-cols-2 rounded-none border-b">
                <TabsTrigger value="quick" className="text-xs">Raccourcis</TabsTrigger>
                <TabsTrigger value="custom" className="text-xs">Personnalisé</TabsTrigger>
              </TabsList>

              <TabsContent value="quick" className="p-3 space-y-2 m-0">
                <div className="grid grid-cols-2 gap-2">
                  {quickPeriods.map((period) => (
                    <Button
                      key={period.value}
                      variant="ghost"
                      size="sm"
                      onClick={period.action}
                      className="h-8 text-xs justify-start hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      {period.label}
                    </Button>
                  ))}
                </div>
                {hasFilters && (
                  <div className="pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      className="w-full h-8 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <X className="h-3 w-3 mr-2" />
                      Réinitialiser
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="custom" className="p-3 space-y-3 m-0">
                <div className="space-y-2">
                  <Label htmlFor="start-date-compact" className="text-xs font-medium">
                    Date de début
                  </Label>
                  <Input
                    id="start-date-compact"
                    type="date"
                    value={localStartDate}
                    onChange={(e) => setLocalStartDate(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date-compact" className="text-xs font-medium">
                    Date de fin
                  </Label>
                  <Input
                    id="end-date-compact"
                    type="date"
                    value={localEndDate}
                    onChange={(e) => setLocalEndDate(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="pt-2 space-y-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleApplyCustom}
                    className="w-full h-8 text-xs font-medium bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
                  >
                    Appliquer la période
                  </Button>
                  {hasFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleReset}
                      className="w-full h-8 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <X className="h-3 w-3 mr-2" />
                      Réinitialiser
                    </Button>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </PopoverContent>
        </Popover>

        {hasFilters && (
          <Badge
            variant="secondary"
            className="h-6 px-2 text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors"
          >
            Filtré
          </Badge>
        )}
      </div>
    );
  }

  // Version complète avec card
  return (
    <Card className={`border-border/50 bg-card/50 backdrop-blur-sm ${className}`}>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Section principale avec les champs de date */}
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="start-date" className="flex items-center gap-2 text-sm font-medium">
                  <Calendar className="h-4 w-4 text-primary" />
                  Date de début
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={localStartDate}
                  onChange={(e) => setLocalStartDate(e.target.value)}
                  className="h-9 border-border/50 focus:border-primary/50 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date" className="text-sm font-medium">
                  Date de fin
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={localEndDate}
                  onChange={(e) => setLocalEndDate(e.target.value)}
                  className="h-9 border-border/50 focus:border-primary/50 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Section raccourcis */}
          <div className="lg:w-auto">
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Clock className="h-4 w-4" />
                Raccourcis
              </Label>
              <div className="flex flex-wrap gap-2">
                {quickPeriods.slice(0, 4).map((period) => (
                  <Button
                    key={period.value}
                    variant="outline"
                    size="sm"
                    onClick={period.action}
                    className="h-8 text-xs px-3 hover:bg-primary/10 hover:text-primary hover:border-primary/50 transition-all duration-200"
                  >
                    {period.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Section actions */}
          <div className="lg:w-auto flex items-end gap-2 pb-[2px]">
            <Button
              variant="default"
              size="sm"
              onClick={handleApplyCustom}
              className="h-9 px-4 text-xs font-medium bg-primary hover:bg-primary/90 text-primary-foreground transition-colors shadow-sm"
            >
              Appliquer
            </Button>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                className="h-9 px-3 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              >
                <X className="h-4 w-4 mr-2" />
                Réinitialiser
              </Button>
            )}
          </div>
        </div>

        {/* Indicateur de période sélectionnée */}
        {hasFilters && (
          <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="font-medium text-primary">Période sélectionnée :</span>
              <span className="text-foreground">{getPeriodText()}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}; 